import {
  ChatMessage,
  ValidationResult,
  MessageStatus,
  ChatError,
  ErrorCode,
} from 'modules/chatbot/types/chatbot.types';
import { Sender, MessageType } from 'modules/chatbot/enums/Sender';

// Validation constants
export const VALIDATION_CONSTANTS = {
  MESSAGE_MIN_LENGTH: 1,
  MESSAGE_MAX_LENGTH: 4000,
  CONVERSATION_TITLE_MIN_LENGTH: 1,
  CONVERSATION_TITLE_MAX_LENGTH: 100,
  MAX_RETRY_ATTEMPTS: 3,
  MIN_TIMESTAMP: 0,
} as const;

// Type guards
export const isValidSender = (value: any): value is Sender => {
  return Object.values(Sender).includes(value);
};

export const isValidMessageStatus = (value: any): value is MessageStatus => {
  const validStatuses: MessageStatus[] = [
    MessageStatus.FAILED,
    MessageStatus.COMPLETED,
    MessageStatus.PENDING,
  ];
  return validStatuses.includes(value);
};

export const isValidMessageType = (value: any): value is MessageType => {
  return Object.values(MessageType).includes(value);
};

export const isValidTimestamp = (timestamp: any): timestamp is number => {
  return (
    typeof timestamp === 'number' &&
    timestamp >= VALIDATION_CONSTANTS.MIN_TIMESTAMP &&
    timestamp <= Date.now() + 86400000
  ); // Allow 1 day in future
};

export const isValidId = (id: any): id is string => {
  return typeof id === 'string' && id.length > 0 && id.length <= 255;
};

// Message validation
export const validateMessageContent = (content: string): ValidationResult => {
  const errors: Record<string, string> = {};

  if (!content || typeof content !== 'string') {
    errors.content = 'Message content is required and must be a string';
  } else {
    const trimmedContent = content.trim();
    if (trimmedContent.length < VALIDATION_CONSTANTS.MESSAGE_MIN_LENGTH) {
      errors.content = `Message must be at least ${VALIDATION_CONSTANTS.MESSAGE_MIN_LENGTH} character long`;
    }
    if (trimmedContent.length > VALIDATION_CONSTANTS.MESSAGE_MAX_LENGTH) {
      errors.content = `Message cannot exceed ${VALIDATION_CONSTANTS.MESSAGE_MAX_LENGTH} characters`;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const validateChatMessage = (
  message: Partial<ChatMessage>
): ValidationResult => {
  const errors: Record<string, string> = {};

  // Validate required fields
  if (!isValidId(message.id)) {
    errors.id = 'Valid message ID is required';
  }

  if (!isValidSender(message.sender)) {
    errors.sender = 'Valid sender is required';
  }

  if (!message.content || typeof message.content !== 'string') {
    errors.content = 'Message content is required';
  } else {
    const contentValidation = validateMessageContent(message.content);
    if (!contentValidation.isValid) {
      Object.assign(errors, contentValidation.errors);
    }
  }

  if (!isValidTimestamp(message.timestamp)) {
    errors.timestamp = 'Valid timestamp is required';
  }

  if (message.status && !isValidMessageStatus(message.status)) {
    errors.status = 'Invalid message status';
  }

  if (
    message.retryCount !== undefined &&
    (typeof message.retryCount !== 'number' ||
      message.retryCount < 0 ||
      message.retryCount > VALIDATION_CONSTANTS.MAX_RETRY_ATTEMPTS)
  ) {
    errors.retryCount = `Retry count must be between 0 and ${VALIDATION_CONSTANTS.MAX_RETRY_ATTEMPTS}`;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

// Error creation utilities
export const createChatError = (
  code: ErrorCode,
  message: string,
  context?: Record<string, any>
): ChatError => {
  return {
    code,
    message,
    timestamp: Date.now(),
    context,
  };
};

export const createValidationError = (
  validationResult: ValidationResult,
  context?: Record<string, any>
): ChatError => {
  const errorMessage = Object.values(validationResult.errors).join('; ');
  return createChatError(
    'VALIDATION_ERROR',
    `Validation failed: ${errorMessage}`,
    { ...context, validationErrors: validationResult.errors }
  );
};

// Sanitization utilities
export const sanitizeMessageContent = (content: string): string => {
  if (typeof content !== 'string') return '';

  return content
    .trim()
    .replace(/\s+/g, ' ') // Replace multiple spaces with single space
    .substring(0, VALIDATION_CONSTANTS.MESSAGE_MAX_LENGTH);
};

export const sanitizeConversationTitle = (title: string): string => {
  if (typeof title !== 'string') return 'Untitled Conversation';

  return (
    title
      .trim()
      .replace(/\s+/g, ' ')
      .substring(0, VALIDATION_CONSTANTS.CONVERSATION_TITLE_MAX_LENGTH) ||
    'Untitled Conversation'
  );
};
